import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Briefcase, Trash2, Clock, RefreshCw, Copy } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Services = () => {
  const [services, setServices] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedAirport, setSelectedAirport] = useState("all"); // Filter by airport
  const [selectedAgency, setSelectedAgency] = useState("all"); // Filter by agency
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString()); // Filter by year
  const [showDuplicatePopup, setShowDuplicatePopup] = useState(false);
  const [priceIncreasePercentage, setPriceIncreasePercentage] = useState("0");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "Basic Assistance",
    default_price: "",
    parking_fee: "0",
    vat_rate: "0",
    duty_hours: "2",
    hours_before_flight: "2",
    agency_specific: false,
    agency_id: "",
    available_to_all_agencies: true,
    excluded_agency_ids: [],
    airport_codes: [],
    valid_until: "",
    auto_add: false
  });
  const [editingService, setEditingService] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [servicesRes, agenciesRes, airportsRes] = await Promise.all([
        axios.get(`${API}/services`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agencies`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/airports?active_only=true`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setServices(servicesRes.data);
      setAgencies(agenciesRes.data);
      setAirports(airportsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const url = editingService ? `${API}/services/${editingService}` : `${API}/services`;
      const method = editingService ? 'put' : 'post';
      
      const response = await axios[method](url, {
        ...formData,
        default_price: parseFloat(formData.default_price),
        duty_hours: parseInt(formData.duty_hours),
        hours_before_flight: parseInt(formData.hours_before_flight)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (editingService) {
        // Check server logs for updated flights count
        toast.success("Service updated successfully! All flights using this service have been automatically updated with the new price and details.", { duration: 5000 });
      } else {
        toast.success("Service created successfully");
      }
      
      setOpen(false);
      setEditingService(null);
      setFormData({ name: "", description: "", category: "Basic Assistance", default_price: "", parking_fee: "0", vat_rate: "0", duty_hours: "2", hours_before_flight: "2", agency_specific: false, agency_id: "", available_to_all_agencies: true, excluded_agency_ids: [], airport_codes: [], valid_until: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save service");
    }
  };

  const handleEdit = (service) => {
    setEditingService(service.id);
    setFormData({
      name: service.name,
      description: service.description || "",
      category: service.category || "Basic Assistance",
      default_price: service.default_price.toString(),
      parking_fee: (service.parking_fee || 0).toString(),
      vat_rate: (service.vat_rate || 0).toString(),
      duty_hours: (service.duty_hours || 2).toString(),
      hours_before_flight: (service.hours_before_flight || 2).toString(),
      agency_specific: service.agency_specific || false,
      agency_id: service.agency_id || "",
      available_to_all_agencies: service.available_to_all_agencies !== undefined ? service.available_to_all_agencies : true,
      excluded_agency_ids: service.excluded_agency_ids || [],
      airport_codes: service.airport_codes || [],
      valid_until: service.valid_until || "",
      auto_add: service.auto_add || false
    });
    setOpen(true);
  };

  const handleDuplicate = async (serviceId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/services/${serviceId}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Service duplicated successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to duplicate service");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/services/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Service deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete service");
    }
  };

  const handleSyncAllServices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/services/sync-all-flights`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.flights_updated > 0) {
        toast.success(`Synced ${response.data.flights_updated} flights with latest service data from ${response.data.services_synced} services`, { duration: 5000 });
      } else {
        toast.success("All services are already up to date!");
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync services");
    }
  };

  const handleDuplicateToNextYear = async () => {
    if (!priceIncreasePercentage || priceIncreasePercentage === "") {
      toast.error("Please enter a price increase percentage");
      return;
    }

    const percentage = parseFloat(priceIncreasePercentage);
    if (isNaN(percentage)) {
      toast.error("Please enter a valid number");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const currentYear = parseInt(selectedYear);
      const nextYear = currentYear + 1;

      const response = await axios.post(
        `${API}/services/duplicate-to-year`,
        {
          source_year: currentYear,
          target_year: nextYear,
          price_increase_percentage: percentage
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(
        `Successfully duplicated ${response.data.duplicated_count} services to ${nextYear} with ${percentage}% price increase`,
        { duration: 5000 }
      );

      setShowDuplicatePopup(false);
      setPriceIncreasePercentage("0");
      setSelectedYear(nextYear.toString());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to duplicate services");
    }
  };

  const getAvailableYears = () => {
    const years = new Set();
    services.forEach(service => {
      if (service.valid_until) {
        const year = new Date(service.valid_until).getFullYear();
        years.add(year);
      }
    });
    // Add current year and next year if not present
    years.add(new Date().getFullYear());
    years.add(new Date().getFullYear() + 1);
    return Array.from(years).sort((a, b) => a - b);
  };

  if (loading) {
    return <div style={{ color: '#B0B0B0' }}>Loading...</div>;
  }

  return (
    <div data-testid="services-page">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF' }}>VIP Services</h1>
            <p style={{ color: '#B0B0B0' }}>Manage available VIP services with Belgian VAT rates</p>
          </div>
          
          {/* Year Filter */}
          <div className="flex items-center gap-2">
            <Label style={{ color: '#E8E8E8' }}>Year:</Label>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[120px]" style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                {getAvailableYears().map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowDuplicatePopup(true)}
            style={{ background: '#FBBF24', color: '#0A1128' }}
          >
            <Copy className="mr-2 h-4 w-4" />
            Duplicate to {parseInt(selectedYear) + 1}
          </Button>
          <Button 
            onClick={handleSyncAllServices}
            style={{ background: '#3B82F6', color: '#FFFFFF' }}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Sync All Services
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button style={{ background: '#FFFFFF', color: '#0A1128' }} data-testid="add-service-button" onClick={() => { setEditingService(null); setFormData({ name: "", description: "", category: "Basic Assistance", default_price: "", parking_fee: "0", vat_rate: "0", duty_hours: "2", hours_before_flight: "2", agency_specific: false, agency_id: "", available_to_all_agencies: true, excluded_agency_ids: [], airport_codes: [], valid_until: "" }); }}>
                <Plus className="mr-2 h-4 w-4" />
                Add Service
              </Button>
            </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
            <DialogHeader>
              <DialogTitle style={{ color: '#FFFFFF' }}>{editingService ? "Edit Service" : "Add New Service"}</DialogTitle>
              <DialogDescription style={{ color: '#B0B0B0' }}>{editingService ? "Update service details" : "Create a new VIP service type"}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" style={{ color: '#E8E8E8' }}>Service Name *</Label>
                <Input id="name" data-testid="service-name" placeholder="e.g., VIP Meet & Greet" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
              <div>
                <Label htmlFor="description" style={{ color: '#E8E8E8' }}>Description</Label>
                <Textarea id="description" data-testid="service-description" placeholder="Service description..." value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
              <div>
                <Label htmlFor="category" style={{ color: '#E8E8E8' }}>Category *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })} required>
                  <SelectTrigger id="category" data-testid="service-category" style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                    <SelectItem value="Basic Assistance">Basic Assistance</SelectItem>
                    <SelectItem value="Additional Services">Additional Services</SelectItem>
                    <SelectItem value="Meet & Greet">Meet & Greet</SelectItem>
                    <SelectItem value="VIP Lounge">VIP Lounge</SelectItem>
                    <SelectItem value="Transportation">Transportation</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="default_price" style={{ color: '#E8E8E8' }}>Default Price (€) *</Label>
                  <Input id="default_price" data-testid="service-price" type="number" step="0.01" placeholder="99.99" value={formData.default_price} onChange={(e) => setFormData({ ...formData, default_price: e.target.value })} required style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
                </div>
                <div>
                  <Label htmlFor="parking_fee" style={{ color: '#E8E8E8' }}>Parking Fee (€)</Label>
                  <Input id="parking_fee" type="number" step="0.01" placeholder="0.00" value={formData.parking_fee} onChange={(e) => setFormData({ ...formData, parking_fee: e.target.value })} style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="vat_rate" style={{ color: '#E8E8E8' }}>VAT Rate (Belgium) *</Label>
                  <Select value={formData.vat_rate} onValueChange={(value) => setFormData({ ...formData, vat_rate: value })}>
                    <SelectTrigger id="vat_rate" data-testid="vat-rate" style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                      <SelectItem value="21">21% (Standard)</SelectItem>
                      <SelectItem value="12">12% (Reduced)</SelectItem>
                      <SelectItem value="6">6% (Super Reduced)</SelectItem>
                      <SelectItem value="0">0% (Exempt)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="duty_hours" style={{ color: '#E8E8E8' }}>Duty Duration *</Label>
                  <Select value={formData.duty_hours} onValueChange={(value) => setFormData({ ...formData, duty_hours: value })}>
                    <SelectTrigger id="duty_hours" data-testid="duty-hours" style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                      <SelectItem value="2">2 hours</SelectItem>
                      <SelectItem value="3">3 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="hours_before_flight" style={{ color: '#E8E8E8' }}>Start Before Flight *</Label>
                  <Select value={formData.hours_before_flight} onValueChange={(value) => setFormData({ ...formData, hours_before_flight: value })}>
                    <SelectTrigger id="hours_before_flight" data-testid="hours-before" style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                      <SelectItem value="2">2 hours before</SelectItem>
                      <SelectItem value="3">3 hours before</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Agency Configuration Section */}
              <div className="space-y-4 p-4 rounded" style={{ background: 'rgba(255, 215, 0, 0.05)', border: '1px solid rgba(255, 215, 0, 0.2)' }}>
                <Label style={{ color: '#FFD700', fontSize: '1rem', fontWeight: '600' }}>Agency Visibility Settings</Label>
                
                <div className="flex items-center space-x-2">
                  <Checkbox id="agency_specific" checked={formData.agency_specific} onCheckedChange={(checked) => setFormData({ ...formData, agency_specific: checked, agency_id: checked ? formData.agency_id : "", available_to_all_agencies: !checked })} />
                  <Label htmlFor="agency_specific" style={{ color: '#E8E8E8' }}>Agency-Specific Service</Label>
                </div>
                {formData.agency_specific && (
                  <div>
                    <Label htmlFor="agency_id" style={{ color: '#E8E8E8' }}>Select Agency *</Label>
                    <Select value={formData.agency_id} onValueChange={(value) => setFormData({ ...formData, agency_id: value })}>
                      <SelectTrigger id="agency_id" data-testid="agency-select" style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                        <SelectValue placeholder="Select agency" />
                      </SelectTrigger>
                      <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                        {agencies.filter(a => a.is_active !== false).map((agency) => (
                          <SelectItem key={agency.id} value={agency.id}>{agency.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="available_to_all_agencies" 
                    checked={formData.available_to_all_agencies} 
                    onCheckedChange={(checked) => setFormData({ ...formData, available_to_all_agencies: checked, excluded_agency_ids: checked ? formData.excluded_agency_ids : [], agency_specific: !checked })} 
                  />
                  <Label htmlFor="available_to_all_agencies" style={{ color: '#E8E8E8' }}>Service for All Agencies</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="auto_add" 
                    checked={formData.auto_add || false} 
                    onCheckedChange={(checked) => setFormData({ ...formData, auto_add: checked })} 
                  />
                  <Label htmlFor="auto_add" style={{ color: '#FFD700' }}>
                    🔄 Auto-Add Service (Can be automatically added/refreshed on flights)
                  </Label>
                </div>
                
                {formData.available_to_all_agencies && (
                  <div>
                    <Label style={{ color: '#E8E8E8' }}>
                      Except Agencies (Optional)
                      <span className="text-xs ml-2" style={{ color: '#B0B0B0' }}>
                        (Select agencies that CANNOT use this service)
                      </span>
                    </Label>
                    <div className="grid grid-cols-2 gap-2 mt-2 p-3 rounded max-h-40 overflow-y-auto" style={{ background: '#0A1128', borderColor: '#4A5A8A', border: '1px solid' }}>
                      {agencies.length === 0 ? (
                        <p className="text-sm col-span-2" style={{ color: '#B0B0B0' }}>No agencies configured</p>
                      ) : (
                        agencies.filter(a => a.is_active !== false).map((agency) => (
                          <div key={agency.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`exclude-agency-${agency.id}`}
                              checked={formData.excluded_agency_ids.includes(agency.id)}
                              onCheckedChange={(checked) => {
                                const updated = checked
                                  ? [...formData.excluded_agency_ids, agency.id]
                                  : formData.excluded_agency_ids.filter(id => id !== agency.id);
                                setFormData({ ...formData, excluded_agency_ids: updated });
                              }}
                            />
                            <Label htmlFor={`exclude-agency-${agency.id}`} className="text-sm cursor-pointer" style={{ color: '#E8E8E8' }}>
                              {agency.name}
                            </Label>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Airport Selection */}
              <div>
                <Label style={{ color: '#E8E8E8' }}>
                  Available at Airports
                  <span className="text-xs ml-2" style={{ color: '#B0B0B0' }}>
                    (Leave empty for all airports)
                  </span>
                </Label>
                <div className="grid grid-cols-2 gap-2 mt-2 p-3 rounded" style={{ background: '#0A1128', borderColor: '#4A5A8A', border: '1px solid' }}>
                  {airports.length === 0 ? (
                    <p className="text-sm col-span-2" style={{ color: '#B0B0B0' }}>No airports configured</p>
                  ) : (
                    airports.sort((a, b) => a.code.localeCompare(b.code)).map((airport) => (
                      <div key={airport.code} className="flex items-center space-x-2">
                        <Checkbox
                          id={`airport-${airport.code}`}
                          checked={formData.airport_codes.includes(airport.code)}
                          onCheckedChange={(checked) => {
                            const updated = checked
                              ? [...formData.airport_codes, airport.code]
                              : formData.airport_codes.filter(c => c !== airport.code);
                            setFormData({ ...formData, airport_codes: updated });
                          }}
                        />
                        <Label htmlFor={`airport-${airport.code}`} className="text-sm cursor-pointer" style={{ color: '#E8E8E8' }}>
                          {airport.code}
                        </Label>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              <div>
                <Label htmlFor="valid_until" style={{ color: '#E8E8E8' }}>Valid Until (optional)</Label>
                <Input id="valid_until" type="date" value={formData.valid_until} onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })} style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
              <Button type="submit" className="w-full" style={{ background: '#FFFFFF', color: '#0A1128' }} data-testid="submit-service">{editingService ? "Update Service" : "Create Service"}</Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filter Section */}
      <div className="mb-6 space-y-4">
        {/* Agency Filter Dropdown */}
        <div className="flex items-center gap-4">
          <Label style={{ color: '#E8E8E8', minWidth: '120px' }}>Filter by Agency:</Label>
          <Select value={selectedAgency} onValueChange={setSelectedAgency}>
            <SelectTrigger className="w-[300px]" style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
              <SelectValue placeholder="Select Agency" />
            </SelectTrigger>
            <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
              <SelectItem value="all">All Agencies</SelectItem>
              <SelectItem value="global">Global Services (Available to All)</SelectItem>
              {agencies
                .filter(agency => agency.id && agency.name)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(agency => (
                  <SelectItem key={agency.id} value={agency.id}>{agency.name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        {/* Airport Filter Buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={selectedAirport === "all" ? "default" : "outline"}
            onClick={() => setSelectedAirport("all")}
            style={selectedAirport === "all" ? { background: '#1E3A8A', color: '#FFFFFF' } : { borderColor: '#4A5A8A', color: '#E8E8E8' }}
          >
            All Airports
          </Button>
          {airports.map((airport) => (
            <Button
              key={airport.code}
              variant={selectedAirport === airport.code ? "default" : "outline"}
              onClick={() => setSelectedAirport(airport.code)}
              style={selectedAirport === airport.code ? { background: '#1E3A8A', color: '#FFFFFF' } : { borderColor: '#4A5A8A', color: '#E8E8E8' }}
            >
              {airport.code} - {airport.name}
            </Button>
          ))}
        </div>
      </div>

      {/* Basic Assistance Services Section */}
      {services.filter(s => {
        const airportMatch = selectedAirport === "all" || !s.airport_codes || s.airport_codes.length === 0 || s.airport_codes.includes(selectedAirport);
        const agencyMatch = selectedAgency === "all" || 
                           (selectedAgency === "global" && (s.available_to_all_agencies || !s.agency_specific)) ||
                           (selectedAgency !== "global" && s.agency_id === selectedAgency);
        const yearMatch = !s.valid_until || new Date(s.valid_until).getFullYear().toString() === selectedYear;
        return airportMatch && agencyMatch && yearMatch && (s.category === "Basic Assistance" || s.name.toLowerCase().includes("basic"));
      }).length > 0 && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#FFD700' }}>Basic Assistance Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services
              .filter(service => {
                const airportMatch = selectedAirport === "all" || !service.airport_codes || service.airport_codes.length === 0 || service.airport_codes.includes(selectedAirport);
                const agencyMatch = selectedAgency === "all" || 
                                   (selectedAgency === "global" && (service.available_to_all_agencies || !service.agency_specific)) ||
                                   (selectedAgency !== "global" && service.agency_id === selectedAgency);
                const yearMatch = !service.valid_until || new Date(service.valid_until).getFullYear().toString() === selectedYear;
                return airportMatch && agencyMatch && yearMatch && (service.category === "Basic Assistance" || service.name.toLowerCase().includes("basic"));
              })
              .map((service) => (
              <Card key={service.id} className="card-hover" style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.3)', backdropFilter: 'blur(12px)' }} data-testid={`service-card-${service.id}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Briefcase className="mr-2 h-5 w-5" style={{ color: '#FFD700' }} />
                      <span style={{ color: '#E8E8E8' }}>{service.name}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(service)} style={{ color: '#60A5FA' }}>Edit</Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDuplicate(service.id)} style={{ color: '#FBBF24' }}>Duplicate</Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(service.id)} data-testid={`delete-service-${service.id}`} style={{ color: '#FF6B6B' }}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {service.description && (
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>{service.description}</p>
                  )}
                  <div className="flex items-center text-sm" style={{ color: '#B0B0B0' }}>
                    <Clock className="mr-2 h-4 w-4" style={{ color: '#FFD700' }} />
                    {service.duty_hours || 2}h duty, starts {service.hours_before_flight || 2}h before flight
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <div>
                      <div className="text-lg font-bold" style={{ color: '#FFFFFF' }}>
                        €{service.default_price?.toFixed(2)}
                      </div>
                      {service.parking_fee > 0 && (
                        <div className="text-sm" style={{ color: '#FBBF24' }}>
                          + €{service.parking_fee?.toFixed(2)} parking
                        </div>
                      )}
                    </div>
                    <div className="text-sm" style={{ color: '#B0B0B0' }}>VAT {service.vat_rate || 21}%</div>
                  </div>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <div className="text-xs p-2 rounded" style={{ background: 'rgba(255, 215, 0, 0.2)', color: '#FFD700' }}>
                      Basic Assistance
                    </div>
                    {service.agency_specific && (
                      <div className="text-xs p-2 rounded" style={{ background: 'rgba(255, 255, 255, 0.1)', color: '#FFFFFF' }}>
                        {(() => {
                          const agency = agencies.find(a => a.id === service.agency_id);
                          return agency ? `${agency.name} Only` : 'Agency-Specific';
                        })()}
                      </div>
                    )}
                    {service.airport_codes && service.airport_codes.length > 0 && (
                      <div className="text-xs p-2 rounded" style={{ background: 'rgba(96, 165, 250, 0.2)', color: '#60A5FA' }}>
                        Airports: {service.airport_codes.join(', ')}
                      </div>
                    )}
                    {(!service.airport_codes || service.airport_codes.length === 0) && (
                      <div className="text-xs p-2 rounded" style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22C55E' }}>
                        All Airports
                      </div>
                    )}
                    {service.valid_until && (
                      <div className="text-xs p-2 rounded" style={{ background: 'rgba(255, 215, 0, 0.2)', color: '#FFD700' }}>
                        Valid until: {service.valid_until}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Extra Services Section */}
      {services.filter(s => {
        const airportMatch = selectedAirport === "all" || !s.airport_codes || s.airport_codes.length === 0 || s.airport_codes.includes(selectedAirport);
        const agencyMatch = selectedAgency === "all" || 
                           (selectedAgency === "global" && (s.available_to_all_agencies || !s.agency_specific)) ||
                           (selectedAgency !== "global" && s.agency_id === selectedAgency);
        const yearMatch = !s.valid_until || new Date(s.valid_until).getFullYear().toString() === selectedYear;
        return airportMatch && agencyMatch && yearMatch && s.category !== "Basic Assistance" && !s.name.toLowerCase().includes("basic");
      }).length > 0 && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#60A5FA' }}>Extra Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services
              .filter(service => {
                const airportMatch = selectedAirport === "all" || !service.airport_codes || service.airport_codes.length === 0 || service.airport_codes.includes(selectedAirport);
                const agencyMatch = selectedAgency === "all" || 
                                   (selectedAgency === "global" && (service.available_to_all_agencies || !service.agency_specific)) ||
                                   (selectedAgency !== "global" && service.agency_id === selectedAgency);
                const yearMatch = !service.valid_until || new Date(service.valid_until).getFullYear().toString() === selectedYear;
                return airportMatch && agencyMatch && yearMatch && service.category !== "Basic Assistance" && !service.name.toLowerCase().includes("basic");
              })
              .map((service) => (
              <Card key={service.id} className="card-hover" style={{ background: 'rgba(30, 42, 74, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)', backdropFilter: 'blur(12px)' }} data-testid={`service-card-${service.id}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Briefcase className="mr-2 h-5 w-5" style={{ color: '#FFFFFF' }} />
                      <span style={{ color: '#E8E8E8' }}>{service.name}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(service)} style={{ color: '#60A5FA' }}>Edit</Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDuplicate(service.id)} style={{ color: '#FBBF24' }}>Duplicate</Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(service.id)} data-testid={`service-card-${service.id}`} style={{ color: '#FF6B6B' }}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {service.description && (
                    <p className="text-sm" style={{ color: '#B0B0B0' }}>{service.description}</p>
                  )}
                  <div className="flex items-center text-sm" style={{ color: '#B0B0B0' }}>
                    <Clock className="mr-2 h-4 w-4" style={{ color: '#FFFFFF' }} />
                    {service.duty_hours || 2}h duty, starts {service.hours_before_flight || 2}h before flight
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <div>
                      <div className="text-lg font-bold" style={{ color: '#FFFFFF' }}>
                        €{service.default_price?.toFixed(2)}
                      </div>
                      {service.parking_fee > 0 && (
                        <div className="text-sm" style={{ color: '#FBBF24' }}>
                          + €{service.parking_fee?.toFixed(2)} parking
                        </div>
                      )}
                    </div>
                    <div className="text-sm" style={{ color: '#B0B0B0' }}>VAT {service.vat_rate || 21}%</div>
                  </div>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {service.agency_specific && (
                      <div className="text-xs p-2 rounded" style={{ background: 'rgba(255, 255, 255, 0.1)', color: '#FFFFFF' }}>
                        {(() => {
                          const agency = agencies.find(a => a.id === service.agency_id);
                          return agency ? `${agency.name} Only` : 'Agency-Specific';
                        })()}
                      </div>
                    )}
                    {service.airport_codes && service.airport_codes.length > 0 && (
                      <div className="text-xs p-2 rounded" style={{ background: 'rgba(96, 165, 250, 0.2)', color: '#60A5FA' }}>
                        Airports: {service.airport_codes.join(', ')}
                      </div>
                    )}
                    {(!service.airport_codes || service.airport_codes.length === 0) && (
                      <div className="text-xs p-2 rounded" style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22C55E' }}>
                        All Airports
                      </div>
                    )}
                    {service.valid_until && (
                      <div className="text-xs p-2 rounded" style={{ background: 'rgba(255, 215, 0, 0.2)', color: '#FFD700' }}>
                        Valid until: {service.valid_until}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {services.length === 0 && (
        <div className="text-center py-12">
          <Briefcase className="h-12 w-12 mx-auto mb-4" style={{ color: '#4A5A8A' }} />
          <p style={{ color: '#B0B0B0' }}>No services found. Create your first service to get started.</p>
        </div>
      )}

      {/* Duplicate to Next Year Popup */}
      {showDuplicatePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }} className="rounded-lg p-6 max-w-md w-full mx-4 border">
            <h2 className="text-xl font-bold mb-4" style={{ color: '#FFFFFF' }}>
              Duplicate Services to {parseInt(selectedYear) + 1}
            </h2>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm mb-3" style={{ color: '#B0B0B0' }}>
                  This will duplicate all services from {selectedYear} to {parseInt(selectedYear) + 1} with updated validity dates and adjusted prices.
                </p>
              </div>

              <div>
                <Label style={{ color: '#E8E8E8' }}>Price Increase Percentage *</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="0"
                    value={priceIncreasePercentage}
                    onChange={(e) => setPriceIncreasePercentage(e.target.value)}
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                  />
                  <span style={{ color: '#E8E8E8' }}>%</span>
                </div>
                <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                  Example: Enter 5 for 5% increase, -10 for 10% decrease, 0 for no change
                </p>
              </div>

              <div className="p-3 rounded" style={{ background: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
                <p className="text-sm" style={{ color: '#FBBF24' }}>
                  <strong>Note:</strong> All duplicated services will have validity set to December 31, {parseInt(selectedYear) + 1}
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleDuplicateToNextYear}
                style={{ background: '#FBBF24', color: '#0A1128' }}
                className="flex-1 hover:opacity-90 font-semibold"
              >
                <Copy className="h-4 w-4 mr-2" />
                Duplicate Services
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowDuplicatePopup(false);
                  setPriceIncreasePercentage("0");
                }}
                style={{ borderColor: '#4A5A8A', color: '#B0B0B0' }}
                className="flex-1 font-semibold"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;
